// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  // browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import debugLog from './common/debugLog';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: 'AIzaSyDYfECrj4e7xUZY1g5wVYZmNQkOXxcSMVw',
  authDomain: 'atactconnect.firebaseapp.com',
  projectId: 'atactconnect',
  storageBucket: 'atactconnect.appspot.com',
  messagingSenderId: '193604998592',
  appId: '1:193604998592:web:ad7d8ad2537b1300052425',
  measurementId: 'G-1G74SR6Q7H',
};

// Initialize Firebase (and secondary app for creating users)
const primaryApp = initializeApp(firebaseConfig);
const secondaryApp = initializeApp(firebaseConfig, 'creatingUsersApp');

/**
 * Initialise primary Firebase Authentication manually with Local Persistence
 */
export const primaryAuth = initializeAuth(primaryApp, {
  persistence: browserLocalPersistence,
});
// Initialise secondary Firebase Authentication and get a reference to the service
export const secondaryAuth = getAuth(secondaryApp);

// Initialise Cloud Firestore and get a reference to the service
export const db = getFirestore(primaryApp);

// only connect to emulator if running on localhost:
if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
  connectAuthEmulator(primaryAuth, 'http://127.0.0.1:9099', {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

// D24 (2026-05-14): callable Functions live in asia-southeast1. Used for
// v2rejectAccount / v2requestAccountInfo (per index.js exports).
export const functionsAsia = getFunctions(primaryApp, 'asia-southeast1');

export const firRejectAccount = async (requestId, decisionReason) => {
  const call = httpsCallable(functionsAsia, 'v2rejectAccount');
  const result = await call({ requestId, decisionReason });
  return result.data;
};

export const firRequestAccountInfo = async (requestId, decisionReason) => {
  const call = httpsCallable(functionsAsia, 'v2requestAccountInfo');
  const result = await call({ requestId, decisionReason });
  return result.data;
};

// rev 110 (2026-05-18) — firCreateUser removed. Was dead code (zero callers
// in src/). Carried the same swallowed-error anti-pattern as firSignIn /
// firUpdateLastSignIn. If a future flow needs Auth user creation from the
// secondaryAuth context, write it fresh with proper async/await + propagation.

/**
 * Checks whether an account with the specified email address is already assigned to the site.
 *
 * (WIP) need to separate the Authentication checking because the same email can be used across different sites (and units) must change the logic
 *
 * @param {string} email The email address to be checked
 * @param {string} siteId The site's unique identifier
 * @returns {Promise<boolean>} A Promise that will be resolved to `true` if the email address is already in use, `false` otherwise
 */
export const firCheckEmailExists = async (email, siteId) => {
  const coll = collection(db, 'accounts');
  const q = query(
    coll,
    where('assignedSite', '==', siteId),
    where('email', '==', email),
  );
  const snapshot = await getCountFromServer(q);

  return !!snapshot.data().count; // forced boolean coercion
};

/**
 * Counts the number of requests in the result set of the specified query
 *
 * Only the final count (not data) is downloaded. Another query would need to be performed to fetch the documents themselves (if they are needed)
 *
 * @param {string} col The collection within the database to count requests from
 * @param {string} siteId The query will only include requests from this site ID
 * @param {number} requestStatus The query will only include requests with this status
 * @returns {Promise<number>} A Promise that will be resolved to the number of documents in the result set of the given query
 */
export const firGetSiteRequestCount = async (col, siteId, requestStatus) => {
  const coll = collection(db, col);
  const q = query(
    coll,
    where('siteId', '==', siteId),
    where('requestStatus', '==', requestStatus),
  );
  const snapshot = await getCountFromServer(q);

  return snapshot.data().count;
};

/**
 * Fetches the site's configuration object, which specifies certain site settings
 *
 * @param {string} siteId The site's unique identifier
 * @returns {Promise<object>} A Promise that will be resolved to an `object` containing the configuration fields (will be empty if document does not exist)
 */
export const firGetSiteConfiguration = async (siteId) => {
  const docRef = doc(db, 'sites', siteId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const dict = docSnap.data();
    return dict.configuration ?? {};
  }

  console.warn('-- No such document');
  return {};
};

/**
 * Fetches the site's display name and slug
 *
 * @param {string} siteId The site's unique identifier
 * @returns {Promise<{name: string, slug: string}>} A Promise that will be resolved to an `object` containing the display name and slug (will be empty strings if document does not exist)
 */
export const firGetSiteNameSlug = async (siteId) => {
  const docRef = doc(db, 'sites', siteId);
  const docSnap = await getDoc(docRef);
  const nameSlug = { name: '', slug: '' };

  if (docSnap.exists()) {
    const dict = docSnap.data();
    nameSlug.name = dict.displayName ?? '';
    nameSlug.slug = dict.slug ?? '';
    return nameSlug;
  }

  console.warn('-- No such document');
  return nameSlug;
};

/**
 * Fetches the site's information object, which specifies certain site details
 *
 * @param {string} siteId The site's unique identifier
 * @returns {Promise<object>} A Promise that will be resolved to an `object` containing the information fields (will be empty if document does not exist)
 */
export const firGetSiteInformation = async (siteId) => {
  const docRef = doc(db, 'sites', siteId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const dict = docSnap.data();
    return dict.information ?? {};
  }

  console.warn('-- No such document');
  return {};
};

/**
 * Fetches the URLs associated with the site
 *
 * change to generated at the frontend (WIP)
 *
 * @param {string} siteId The site's unique identifier
 * @returns {Promise<object>} A Promise that will be resolved to an `object` containing the site URLs (will be empty if document does not exist)
 */
export const firGetSiteURL = async (siteId) => {
  const docRef = doc(db, 'sites', siteId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const dict = docSnap.data();
    return dict.siteURL ?? {};
  }

  console.warn('-- No such document');
  return {};
};

/**
 * Fetches the assigned site ID of the user
 *
 * The Web Storage API is not secure enough to store such critical user information
 *
 * @param {string} uid The authenticated user's unique identifier
 * @returns {Promise<string>} A Promise that will be resolved to the assigned site ID (will be empty if document does not exist)
 */
export const firGetUserAssignedSiteId = async (uid) => {
  const docRef = doc(db, 'accounts', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const dict = docSnap.data();
    return dict.assignedSite ?? '';
  }

  console.warn('-- No such document');
  return '';
};

/**
 * Fetches a document containing the user's data (using their unique identifier)
 *
 * @param {string} uid The authenticated user's unique identifier
 * @returns {Promise<DocumentData>} A Promise that will be resolved to an `object` containing all fields in the document (will be empty if document does not exist)
 */
export const firGetUserData = async (uid) => {
  const docRef = doc(db, 'accounts', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data();
  }

  console.warn('-- No such document');
  return {};
};

/**
 * unused for now
 *
 * @param {string} username
 * @param {string} password
 */
// rev 110 (2026-05-18) — firSignIn removed. Was dead code (zero callers;
// signIn.jsx uses primaryAuth + signInWithEmailAndPassword directly).
// Carried the same async-without-await + swallowed-.catch anti-pattern
// as rev 109's firUpdateSiteConfiguration fix.

/**
 * Updates the `lastSignIn` field of the currently signed in user
 *
 * @param {string} uid The authenticated user's unique identifier
 * @param {string} lastSignIn The date and time of the sign in, using `createISODateTime()`
 */
// rev 110 (2026-05-18) — fire-and-forget semantics preserved (lastSignIn
// is informational; a failed update shouldn't block sign-in navigation),
// BUT actually await the write + log errors properly. Pre-rev-110 the
// function was declared async but didn't await, returning undefined
// immediately; the caller's .then fired before the write even attempted.
export const firUpdateLastSignIn = async (uid, lastSignIn) => {
  try {
    await updateDoc(doc(db, 'accounts', uid), { lastSignIn });
    debugLog('-- firUpdateLastSignIn success');
  } catch (error) {
    // Don't propagate — caller chains .then(() => navigate('/home')) and
    // we want navigation to proceed even if lastSignIn write fails.
    console.warn('-- firUpdateLastSignIn failed:', error.code, error.message);
  }
};

/**
 * Update a small whitelist of self-editable user-profile fields on
 * `accounts/{uid}`. Used by Profile.jsx (rev 83). Caller is always the
 * currently-signed-in user (no cross-user write — Firestore rules will
 * lock this down once we tighten them in `firestore.rules.target`).
 *
 * @param {string} uid
 * @param {{ fullname?: string, contactNumber?: string }} patch
 */
/**
 * rev 85/86: transition a Pending staff account (accounts/{uid}.status===0
 * post-rev-86 alignment) to Active (status===1) and stamp `acceptedAt`
 * with serverTimestamp. Called from MA web home.jsx's auth listener on
 * every sign-in — idempotent: only writes when status is actually
 * Pending.
 *
 * Status enum (rev 86):
 *   0 Pending — invited but never signed in
 *   1 Active  — has signed in at least once
 *   2 Deactivated — removed by SuperAdmin
 *
 * @param {string} uid
 */
export const firMarkSignInAccepted = async (uid) => {
  if (!uid) return;
  const docRef = doc(db, 'accounts', uid);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  if (data.status !== 0) return; // 0 = Pending under rev 86 alignment
  await updateDoc(docRef, {
    status: 1,
    acceptedAt: serverTimestamp(),
  });
  debugLog('-- firMarkSignInAccepted Pending->Active', uid);
};

export const firUpdateOwnProfile = async (uid, patch) => {
  const docRef = doc(db, 'accounts', uid);
  // Only allow whitelisted keys (defensive against an upstream typo).
  const allowed = {};
  if (typeof patch.fullname === 'string') allowed.fullname = patch.fullname;
  if (typeof patch.contactNumber === 'string')
    allowed.contactNumber = patch.contactNumber;
  if (Object.keys(allowed).length === 0) return;
  await updateDoc(docRef, allowed);
  debugLog('-- firUpdateOwnProfile success', uid, Object.keys(allowed));
};

/**
 * Updates the site's configuration object with the current user-specified values
 *
 * Each setting should be validated by using Cloud Firestore Security Rules
 *
 * @param {string} siteId The site's unique identifier
 * @param {object} siteParams An object that contains the values to be updated for the site configuration
 * @param {number} siteParams.householdTenantLimit The maximum number of users attached to a unit number
 * @param {number} siteParams.kioskButtonCooldown ddd
 * @param {number} siteParams.kioskNotificationGracePeriod fff
 * @param {boolean} siteParams.tenantAccountApprove Whether creating tenant accounts require management approval (always true for Owner accounts)
 * @param {boolean} siteParams.useTwilioNotification Whether to use Twilio for SMS notifications (paid service)
 * @param {number} siteParams.visitorMaxDays The maximum number of days a visitor can stay at the site
 */
export const firUpdateSiteConfiguration = async (
  siteId,
  {
    householdTenantLimit,
    kioskButtonCooldown,
    kioskNotificationGracePeriod,
    tenantAccountApprove,
    useTwilioNotification,
    visitorMaxDays,
  },
) => {
  // rev 109 (2026-05-18) — fix the "success toast on permission-denied" bug.
  // Pre-rev-109 this function had two problems:
  //   1. The `updateDoc(...).then(...).catch(...)` chain was NOT awaited,
  //      so the outer async function returned undefined immediately. The
  //      caller's `.then` always fired before the write even attempted.
  //   2. The inner `.catch` swallowed errors (logged but didn't rethrow),
  //      so permission-denied / network errors never reached the caller.
  // Fix: await + let errors propagate. Caller's existing `.catch` handles
  // the toast.
  const docRef = doc(db, 'sites', siteId);
  await updateDoc(docRef, {
    'configuration.householdTenantLimit': householdTenantLimit,
    'configuration.kioskButtonCooldown': kioskButtonCooldown,
    'configuration.kioskNotificationGracePeriod': kioskNotificationGracePeriod,
    'configuration.tenantAccountApprove': tenantAccountApprove,
    'configuration.useTwilioNotification': useTwilioNotification,
    'configuration.visitorMaxDays': visitorMaxDays,
  });
  debugLog('-- firUpdateSiteConfiguration success');
};

// ─── Blocks ──────────────────────────────────────────────────────────

/**
 * @param {string} siteId
 * @returns {Promise<Array>} Array of block objects with `id` field
 */
export const firGetBlocks = async (siteId) => {
  const colRef = collection(db, 'sites', siteId, 'blocks');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * @param {string} siteId
 * @param {string} blockCode Used as the document ID
 * @param {Object} blockData { blockName, createdAt }
 */
export const firAddBlock = async (siteId, blockCode, blockData) => {
  const docRef = doc(db, 'sites', siteId, 'blocks', blockCode);
  await setDoc(docRef, blockData);
  debugLog('-- firAddBlock success');
};

/**
 * @param {string} siteId
 * @param {string} blockCode
 * @param {Object} blockData Fields to update
 */
export const firUpdateBlock = async (siteId, blockCode, blockData) => {
  const docRef = doc(db, 'sites', siteId, 'blocks', blockCode);
  await updateDoc(docRef, blockData);
  debugLog('-- firUpdateBlock success');
};

/**
 * Deletes a block and all its unit documents
 * @param {string} siteId
 * @param {string} blockCode
 */
export const firDeleteBlock = async (siteId, blockCode) => {
  const unitsRef = collection(
    db,
    'sites',
    siteId,
    'blocks',
    blockCode,
    'units',
  );
  const snapshot = await getDocs(unitsRef);
  const batch = writeBatch(db);
  snapshot.docs.forEach((d) => batch.delete(d.ref));
  batch.delete(doc(db, 'sites', siteId, 'blocks', blockCode));
  await batch.commit();
  debugLog('-- firDeleteBlock success');
};

// ─── Units ───────────────────────────────────────────────────────────

/**
 * @param {string} siteId
 * @param {string} blockCode
 * @returns {Promise<Array>} Array of unit objects with `id` field
 */
export const firGetUnits = async (siteId, blockCode) => {
  const colRef = collection(db, 'sites', siteId, 'blocks', blockCode, 'units');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Batch-creates many unit documents at once (up to 500 per batch)
 * @param {string} siteId
 * @param {string} blockCode
 * @param {Array<{unitId: string, level: string, unit: string}>} unitsList
 */
export const firBatchAddUnits = async (siteId, blockCode, unitsList) => {
  const batchSize = 500;
  const chunks = [];
  for (let i = 0; i < unitsList.length; i += batchSize) {
    chunks.push(unitsList.slice(i, i + batchSize));
  }
  const commitPromises = chunks.map((chunk) => {
    const batch = writeBatch(db);
    chunk.forEach(({ unitId, level, unit }) => {
      const unitRef = doc(
        db,
        'sites',
        siteId,
        'blocks',
        blockCode,
        'units',
        unitId,
      );
      batch.set(unitRef, {
        level,
        unit,
        accountIds: [],
        createdAt: new Date().toISOString().split('T')[0],
      });
    });
    return batch.commit();
  });
  await Promise.all(commitPromises);
  debugLog(`-- firBatchAddUnits success (${unitsList.length} units)`);
};

/**
 * @param {string} siteId
 * @param {string} blockCode
 * @param {Object} unitData { unitId, level, unit }
 */
export const firAddUnit = async (
  siteId,
  blockCode,
  { unitId, level, unit },
) => {
  const unitRef = doc(
    db,
    'sites',
    siteId,
    'blocks',
    blockCode,
    'units',
    unitId,
  );
  await setDoc(unitRef, {
    level,
    unit,
    accountIds: [],
    createdAt: new Date().toISOString().split('T')[0],
  });
  debugLog('-- firAddUnit success');
};

/**
 * Lists accounts for a site (for linking to units).
 * @param {string} siteId
 * @returns {Promise<Array<{ id: string, fullname?: string, email?: string }>>}
 */
export const firListAccountsBySite = async (siteId) => {
  const coll = collection(db, 'accounts');
  // V2 schema: accounts use the `assignedSite` field (UUID), not the legacy
  // `siteName` (which carried the display name).
  //
  // Sort is intentionally client-side. Adding `orderBy('fullname','asc')` to
  // this query would require a composite (assignedSite + fullname) Firestore
  // index that's awkward to provision during testing. List size per site is
  // small (low hundreds at most) so client-sort cost is negligible.
  const q = query(coll, where('assignedSite', '==', siteId));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  docs.sort((a, b) => {
    const fa = (a.fullname || '').toLowerCase();
    const fb = (b.fullname || '').toLowerCase();
    return fa.localeCompare(fb);
  });
  return docs;
};

/**
 * Lists all accounts assigned to a specific block within a site.
 * Used by the SiteConfig Blocks tab to show which residents currently
 * live in each unit (grouped client-side by unitNumber).
 *
 * D24 (2026-05-13): replaces the old Link/Unlink reverse-mapping approach.
 * Instead of maintaining an `accountIds` array on each unit doc, we derive
 * the resident list from the canonical `accounts.assignedSite/blockNumber/
 * unitNumber` fields — no manual writes, always in sync with reality.
 *
 * No orderBy on the server (would require a composite index); list is
 * sorted client-side by unitNumber then fullname.
 *
 * @param {string} siteId
 * @param {string} blockCode
 * @returns {Promise<Array<{ id, fullname?, email?, blockNumber, unitNumber, ... }>>}
 */
export const firListAccountsByBlock = async (siteId, blockCode) => {
  const coll = collection(db, 'accounts');
  const q = query(
    coll,
    where('assignedSite', '==', siteId),
    where('blockNumber', '==', blockCode),
  );
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  docs.sort((a, b) => {
    const unitCmp = (a.unitNumber || '').localeCompare(b.unitNumber || '');
    if (unitCmp !== 0) return unitCmp;
    return (a.fullname || '')
      .toLowerCase()
      .localeCompare((b.fullname || '').toLowerCase());
  });
  return docs;
};

/**
 * @param {string} siteId
 * @param {string} blockCode
 * @param {string} unitId
 */
export const firDeleteUnit = async (siteId, blockCode, unitId) => {
  const unitRef = doc(
    db,
    'sites',
    siteId,
    'blocks',
    blockCode,
    'units',
    unitId,
  );
  await deleteDoc(unitRef);
  debugLog('-- firDeleteUnit success');
};

// ─── Vehicles ────────────────────────────────────────────────────────

/**
 * @param {string} siteId
 * @returns {Promise<Array>} Array of vehicle objects with `id` (plate number)
 */
export const firGetVehicles = async (siteId) => {
  const colRef = collection(db, 'sites', siteId, 'vehicleList');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * @param {string} siteId
 * @param {string} plateNumber Used as document ID
 * @param {Object} vehicleData
 */
export const firAddVehicle = async (siteId, plateNumber, vehicleData) => {
  const docRef = doc(db, 'sites', siteId, 'vehicleList', plateNumber);
  await setDoc(docRef, vehicleData);
  debugLog('-- firAddVehicle success');
};

/**
 * @param {string} siteId
 * @param {string} plateNumber
 * @param {Object} vehicleData Fields to update
 */
export const firUpdateVehicle = async (siteId, plateNumber, vehicleData) => {
  const docRef = doc(db, 'sites', siteId, 'vehicleList', plateNumber);
  await updateDoc(docRef, vehicleData);
  debugLog('-- firUpdateVehicle success');
};

/**
 * @param {string} siteId
 * @param {string} plateNumber
 */
export const firDeleteVehicle = async (siteId, plateNumber) => {
  const docRef = doc(db, 'sites', siteId, 'vehicleList', plateNumber);
  await deleteDoc(docRef);
  debugLog('-- firDeleteVehicle success');
};

// ─── Door Staff Cards ───────────────────────────────────────────────
//
// rev 119 (2026-05-19) — RFID whitelist per site.  Previously each Pi
// kept this list as an on-disk staff.txt file edited by SSH; now the
// MA web SiteConfig → "Door Staff Cards" tab writes Firestore, the
// Pi polls v2getDoorStaffCards every N minutes, and the local file
// becomes a cache rather than the source of truth.
//
// Schema (sites/{siteId}/doorStaffCards/{cardNo}):
//   {
//     name:       string,            // display name shown in console log on swipe
//     addedBy:    uid,                // who added the card
//     addedAt:    Timestamp,
//     updatedAt:  Timestamp,
//     id?:        number,             // optional legacy ordering; auto-synthesized BE-side
//   }
// Doc id IS the card UID (the 10-digit number the RFID reader emits).
// Using the UID as primary key prevents duplicate cards by construction
// and lets the BE return a stable, deduplicated array.

/**
 * @param {string} siteId
 * @returns {Promise<Array<{cardNo, name, addedBy?, addedAt?, updatedAt?}>>}
 */
export const firListDoorStaffCards = async (siteId) => {
  const colRef = collection(db, 'sites', siteId, 'doorStaffCards');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ cardNo: d.id, ...d.data() }));
};

/**
 * @param {string} siteId
 * @param {string} cardNo Used as document ID — the 10-digit RFID UID
 * @param {string} name Display name (logged on swipe)
 * @param {string} addedBy uid of the admin adding the card
 */
export const firAddDoorStaffCard = async (siteId, cardNo, name, addedBy) => {
  const docRef = doc(db, 'sites', siteId, 'doorStaffCards', cardNo);
  await setDoc(docRef, {
    name,
    addedBy,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  debugLog('-- firAddDoorStaffCard success', siteId, cardNo);
};

/**
 * @param {string} siteId
 * @param {string} cardNo
 * @param {Object} patch Fields to update (typically just { name })
 */
export const firUpdateDoorStaffCard = async (siteId, cardNo, patch) => {
  const docRef = doc(db, 'sites', siteId, 'doorStaffCards', cardNo);
  await updateDoc(docRef, { ...patch, updatedAt: serverTimestamp() });
  debugLog('-- firUpdateDoorStaffCard success', siteId, cardNo);
};

/**
 * @param {string} siteId
 * @param {string} cardNo
 */
export const firDeleteDoorStaffCard = async (siteId, cardNo) => {
  const docRef = doc(db, 'sites', siteId, 'doorStaffCards', cardNo);
  await deleteDoc(docRef);
  debugLog('-- firDeleteDoorStaffCard success', siteId, cardNo);
};

// ─── Plate Cameras (rev 123.3) ──────────────────────────────────────
//
// Maps a license-plate camera's `cam_id` (MAC or vendor UID) to the
// (siteId, doorId) it controls.  carplatescanneragent reads this in
// resolveDoorIdForCamera(camId) — see PLATE_SCANNER.md §4.
//
// Schema (plateCameras/{cam_id}):
//   {
//     cam_id   : string  (also the doc id, duplicated as a field for queryability)
//     siteId   : string  (Firestore site UUID — used as the where() filter)
//     doorId   : string  (channel code from sites/{siteId}.information.doorChannel)
//     label    : string  (human description, e.g. "Front gate entry cam")
//     addedBy  : uid
//     addedAt  : Timestamp
//     updatedAt: Timestamp
//   }
//
// Top-level collection (not under sites/) because the agent's lookup
// path is keyed by cam_id alone — it doesn't know which site to query
// until it reads the doc.  Per-site filtering happens client-side in
// the MA web tab (firListPlateCamerasForSite).

export const firListPlateCamerasForSite = async (siteId) => {
  const colRef = collection(db, 'plateCameras');
  const q = query(colRef, where('siteId', '==', siteId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * @param {string} camId     The camera's MAC/UID — used as the doc id.
 * @param {Object} payload   { siteId, doorId, label }
 * @param {string} addedBy   uid
 */
export const firAddPlateCamera = async (camId, payload, addedBy) => {
  const docRef = doc(db, 'plateCameras', camId);
  await setDoc(docRef, {
    cam_id: camId,
    siteId: String(payload.siteId || '').trim(),
    doorId: String(payload.doorId || '').trim(),
    label: String(payload.label || '').trim(),
    addedBy,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  debugLog('-- firAddPlateCamera success', camId);
};

/**
 * @param {string} camId
 * @param {Object} patch  Fields to update (e.g. label, doorId)
 */
export const firUpdatePlateCamera = async (camId, patch) => {
  const docRef = doc(db, 'plateCameras', camId);
  await updateDoc(docRef, { ...patch, updatedAt: serverTimestamp() });
  debugLog('-- firUpdatePlateCamera success', camId);
};

export const firDeletePlateCamera = async (camId) => {
  const docRef = doc(db, 'plateCameras', camId);
  await deleteDoc(docRef);
  debugLog('-- firDeletePlateCamera success', camId);
};

// ─── Door Events (live listener for Activity tab) ──────────────────
//
// rev 122 (2026-05-19) — read side of the doorEvents collection
// (writes happen from mobile + Pi webhook). The SiteConfig "Door
// Activity" tab uses this to render per-door summary cards + a live
// event table that auto-updates as new opens land.
//
// Schema: see DOOR_EVENT_CONTRACT.md + functions/doorEventWebhook.js.
// We don't define here — just observe.

/**
 * Subscribe to the most recent `lim` doorEvents for `siteId`, ordered
 * by createdAt desc. Calls `onUpdate(events)` immediately with the
 * current snapshot, then again on every Firestore change.
 *
 * @param {string} siteId
 * @param {(events: Array<{id, ...}>) => void} onUpdate
 * @param {{limit?: number, onError?: (err: Error) => void}} [opts]
 * @returns {() => void} unsubscribe — call to detach the listener
 */
export const firListenSiteDoorEvents = (siteId, onUpdate, opts = {}) => {
  const lim = opts.limit ?? 50;
  const onError =
    opts.onError ??
    ((err) => console.error('-- doorEvents listener error:', err));
  const colRef = collection(db, 'doorEvents');
  const q = query(
    colRef,
    where('siteId', '==', siteId),
    orderBy('createdAt', 'desc'),
    limit(lim),
  );
  return onSnapshot(
    q,
    (snap) => {
      const events = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onUpdate(events);
    },
    onError,
  );
};

/**
 * Subscribe to door heartbeats for `siteId` (rev 121.1).  Each Pi
 * writes `doorHeartbeats/{deviceId}` every ~60s with a serverTimestamp;
 * this listener fires whenever any of those docs changes. UI computes
 * Online/Offline by comparing lastSeenAt against a freshness threshold
 * (default 3 min — see SiteConfigDoorStatus).
 *
 * @returns {() => void} unsubscribe
 */
export const firListenDoorHeartbeats = (siteId, onUpdate, opts = {}) => {
  const onError =
    opts.onError ??
    ((err) => console.error('-- doorHeartbeats listener error:', err));
  const colRef = collection(db, 'doorHeartbeats');
  const q = query(colRef, where('siteId', '==', siteId));
  return onSnapshot(
    q,
    (snap) => {
      const beats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onUpdate(beats);
    },
    onError,
  );
};

// ─── Door Keypad PINs ───────────────────────────────────────────────
//
// rev 120 (2026-05-19) — Firestore-managed keypad PINs.  Earlier the
// 4-digit PIN was hardcoded per-Pi in config.json; this lets ATACT
// staff create temporary PINs (e.g. for cleaners, contractors) with
// optional expiry, and revoke them remotely without an SSH session.
//
// Schema (sites/{siteId}/doorKeypadPins/{pinId}):
//   {
//     pin:         string,            // digits the keypad emits
//     label:       string,            // human description (logged on use)
//     doorId:      string | null,     // null = applies to all doors at this site
//     expiresAt:   Timestamp | null,  // null = never expires
//     createdBy:   uid,
//     createdAt:   Timestamp,
//     updatedAt:   Timestamp,
//   }
// Doc id is a UUID so the PIN itself can be edited without re-keying
// the doc. The static `keypadPin` value in each Pi's config.json
// continues to work as a non-expiring master fallback — that file
// is only readable to someone who already controls the Pi.

/**
 * @param {string} siteId
 * @returns {Promise<Array<{id, pin, label, doorId, expiresAt, ...}>>}
 */
export const firListDoorKeypadPins = async (siteId) => {
  const colRef = collection(db, 'sites', siteId, 'doorKeypadPins');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * @param {string} siteId
 * @param {Object} payload { pin, label, doorId?, expiresAt? (Date or null) }
 * @param {string} createdBy uid
 * @returns {Promise<string>} The generated doc id (uuid-ish)
 */
export const firAddDoorKeypadPin = async (siteId, payload, createdBy) => {
  const colRef = collection(db, 'sites', siteId, 'doorKeypadPins');
  const ref = await addDoc(colRef, {
    pin: String(payload.pin || '').trim(),
    label: String(payload.label || '').trim(),
    doorId: payload.doorId || null,
    expiresAt: payload.expiresAt || null, // pass a Firestore Timestamp or null
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  debugLog('-- firAddDoorKeypadPin success', siteId, ref.id);
  return ref.id;
};

/**
 * @param {string} siteId
 * @param {string} pinId Doc id from firListDoorKeypadPins
 * @param {Object} patch Fields to update (pin / label / doorId / expiresAt)
 */
export const firUpdateDoorKeypadPin = async (siteId, pinId, patch) => {
  const docRef = doc(db, 'sites', siteId, 'doorKeypadPins', pinId);
  await updateDoc(docRef, { ...patch, updatedAt: serverTimestamp() });
  debugLog('-- firUpdateDoorKeypadPin success', siteId, pinId);
};

/**
 * @param {string} siteId
 * @param {string} pinId
 */
export const firDeleteDoorKeypadPin = async (siteId, pinId) => {
  const docRef = doc(db, 'sites', siteId, 'doorKeypadPins', pinId);
  await deleteDoc(docRef);
  debugLog('-- firDeleteDoorKeypadPin success', siteId, pinId);
};

// ─── Door Channels ──────────────────────────────────────────────────
//
// Schema (rev 76, 2026-05-15):
//   sites/{siteId}.information.doorChannel = {
//     "101": { isVisitorEntry: bool, label: string },
//     "102": { isVisitorEntry: bool, label: string },
//     ...
//   }
//
// Channel codes (101, 102, ...) are immutable — they correspond to
// physical hardware installed on-site. Adding / removing channels is
// done out-of-band during physical install. Admin UI can only edit
// `label` (display name) and `isVisitorEntry` (whether visitors are
// allowed to trigger this door from the visitor flow).

/**
 * @param {string} siteId
 * @returns {Promise<Object>} Map of channel code -> { isVisitorEntry, label }
 */
export const firGetDoorChannels = async (siteId) => {
  const docRef = doc(db, 'sites', siteId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data().information?.doorChannel ?? {};
  }
  console.warn('-- No such document');
  return {};
};

/**
 * Update a single channel's editable fields. Channel code itself is
 * not writable from MA web — only `label` and `isVisitorEntry`.
 *
 * @param {string} siteId
 * @param {string} channelCode  e.g. "101"
 * @param {{ label: string, isVisitorEntry: boolean }} fields
 */
export const firUpdateDoorChannel = async (siteId, channelCode, fields) => {
  const docRef = doc(db, 'sites', siteId);
  await updateDoc(docRef, {
    [`information.doorChannel.${channelCode}.label`]: fields.label,
    [`information.doorChannel.${channelCode}.isVisitorEntry`]:
      fields.isVisitorEntry,
  });
  debugLog('-- firUpdateDoorChannel success');
};

// ─── SuperAdmin assignment ─────────────────────────────────────────
//
// Added rev 79 (2026-05-15). Manage Sites tab calls the v2assignSuperAdmin
// callable which does the Auth + Firestore + email work server-side via
// the Admin SDK. Client just packages the payload and reflects the
// result back to the UI.
//
// Declared above firListAllSites because the latter calls
// firGetSiteSuperAdmin to inline the assignee in each row.

export const firGetSiteSuperAdmin = async (siteId) => {
  // Find the active SuperAdmin for the site, if any. SuperAdmin = accessLevel 11.
  const q = query(
    collection(db, 'accounts'),
    where('assignedSite', '==', siteId),
    where('accessLevel', '==', 11),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() || {};
  return {
    uid: d.id,
    email: data.email || '',
    fullname: data.fullname || '',
    contactNumber: data.contactNumber || '',
  };
};

export const firAssignSuperAdmin = async (
  siteId,
  email,
  fullname,
  contactNumber = '',
) => {
  const call = httpsCallable(functionsAsia, 'v2assignSuperAdmin');
  const result = await call({ siteId, email, fullname, contactNumber });
  return result.data; // { uid, action, demotedEmail? }
};

/**
 * rev 105 (2026-05-18) — destructive site delete. Root-only.
 *
 * Caller must pass `confirmAck` exactly equal to `DELETE ${siteId}`
 * or the callable refuses with `failed-precondition`. The UI builds
 * this from the type-to-confirm box.
 *
 * Cascade:
 *   - All accounts on the site → status=Deactivated, assignedSite=''
 *   - sites/{siteId} doc + every subcollection (blocks/*, units/*)
 *     → recursive delete
 *   - accountRequests / preRegister / onsite → NOT touched (stale
 *     references left for historical record; future scrub script)
 *
 * @returns { ok, accountsDeactivated, blocksRemoved, unitsRemoved }
 */
export const firDeleteSite = async (siteId) => {
  const call = httpsCallable(functionsAsia, 'v2deleteSite');
  const result = await call({ siteId, confirmAck: `DELETE ${siteId}` });
  return result.data;
};

// ─── Site Staff (per-site admins) ──────────────────────────────────
//
// rev 80 (2026-05-15). Used by SiteConfig → Site Staff tab.

/**
 * @returns array of { uid, email, fullname, contactNumber, accessLevel,
 *   accountType, status }, sorted by accessLevel asc then fullname.
 *
 * Includes the SuperAdmin (11) AND Admins (12). Status 0 (deactivated)
 * still listed so admin can re-invite if needed.
 */
export const firListSiteStaff = async (siteId) => {
  const q = query(
    collection(db, 'accounts'),
    where('assignedSite', '==', siteId),
    where('accessLevel', 'in', [11, 12]),
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => {
    const data = d.data() || {};
    return {
      uid: d.id,
      email: data.email || '',
      fullname: data.fullname || '',
      contactNumber: data.contactNumber || '',
      accessLevel: data.accessLevel,
      accountType: data.accountType || '',
      status: data.status === undefined ? 1 : data.status,
      // rev 85: lifecycle timestamps. invitedAt set on v2inviteAdmin /
      // v2assignSuperAdmin / v2resendAdminInvite. acceptedAt set on
      // first sign-in (Pending -> Active transition).
      invitedAt: data.invitedAt ?? null,
      acceptedAt: data.acceptedAt ?? null,
      // rev 107 — debug toggle. When true, the rev 81 mobile staff-redirect
      // gate lets this account past the resident-app block. Default false.
      isDebugEnabled: data.isDebugEnabled === true,
    };
  });
  rows.sort((a, b) => {
    if (a.accessLevel !== b.accessLevel) return a.accessLevel - b.accessLevel;
    return a.fullname.localeCompare(b.fullname);
  });
  return rows;
};

export const firInviteAdmin = async (
  siteId,
  email,
  fullname,
  contactNumber = '',
) => {
  const call = httpsCallable(functionsAsia, 'v2inviteAdmin');
  const result = await call({ siteId, email, fullname, contactNumber });
  return result.data; // { uid, action }
};

/**
 * rev 107 (2026-05-18) — toggle the mobile staff-app escape hatch.
 *
 * `accounts/{uid}.isDebugEnabled = true` lets that account past the
 * rev 81 mobile staff-redirect gate. Use case: ATACT engineers /
 * QA need to sign into the resident app as a staff account for
 * end-to-end testing without permanently demoting their accessLevel.
 *
 * Writes _lastWriteBy so the rev 90 audit trigger attributes the
 * change. Note: rev 90 trigger only watches accessLevel/status/
 * assignedSite/accountType today; the isDebugEnabled change won't
 * emit an audit row. Acceptable — debug toggle is low-stakes; if we
 * ever need a trail extend AUDIT_FIELDS.
 *
 * Direct Firestore write (no callable). UI-side gate is the SuperAdmin
 * Site Staff tab — Firestore rules are open today (ROADMAP §3.2)
 * so anyone authenticated could in theory write this field. SuperAdmin
 * UI is the gate.
 */
export const firSetIsDebugEnabled = async (uid, value, byUid) => {
  await updateDoc(doc(db, 'accounts', uid), {
    isDebugEnabled: !!value,
    _lastWriteBy: byUid,
  });
  debugLog('-- firSetIsDebugEnabled', uid, !!value);
};

export const firRemoveAdmin = async (siteId, targetUid) => {
  const call = httpsCallable(functionsAsia, 'v2removeAdmin');
  const result = await call({ siteId, targetUid });
  return result.data;
};

export const firResendAdminInvite = async (siteId, targetUid) => {
  const call = httpsCallable(functionsAsia, 'v2resendAdminInvite');
  const result = await call({ siteId, targetUid });
  return result.data;
};

// ─── All Sites (staff / root only) ─────────────────────────────────
//
// Added rev 77 (2026-05-15) for the "Manage Sites" tab gated behind
// the `root: true` custom claim. These helpers operate cross-site so
// they must only be called from screens that are themselves root-
// gated. Firestore rules will eventually enforce server-side; for now
// the gate is frontend-only (see V2_WIP rev 77 security note).
//
// NB: There's a parallel set of admin-scoped helpers higher up
// (firGetDoorChannels / firUpdateDoorChannel) that admins use to edit
// only label + isVisitorEntry on their own site. The helpers below
// are for STAFF who install / decommission channels.

/**
 * @returns {Promise<Array<{ id, displayName, slug, active, address,
 *   channelCount, createdAt }>>} all sites sorted by displayName.
 */
export const firListAllSites = async () => {
  const snap = await getDocs(collection(db, 'sites'));
  // Parallel: pull the active SuperAdmin (accessLevel 11) for each site
  // so the list view can show the assignee inline. One indexed query
  // per site is fine for the staff-only screen (handful of sites).
  const superAdminPromises = snap.docs.map(async (d) => {
    try {
      const sa = await firGetSiteSuperAdmin(d.id);
      return [d.id, sa];
    } catch (e) {
      return [d.id, null];
    }
  });
  const superAdminMap = Object.fromEntries(
    await Promise.all(superAdminPromises),
  );

  const rows = snap.docs.map((d) => {
    const data = d.data() || {};
    const channels = data.information?.doorChannel ?? {};
    const cfg = data.configuration ?? {};
    return {
      id: d.id,
      displayName: data.displayName ?? '',
      slug: data.slug ?? '',
      active: data.active === true,
      address: data.information?.address ?? '',
      siteType: data.information?.siteType ?? '',
      channelCount: Object.keys(channels).length,
      createdAt: data.metadata?.createdAt ?? null,
      superAdmin: superAdminMap[d.id] || null,
      // Inline configuration block so the Edit modal can pre-populate
      // without an extra round-trip per row.
      config: {
        householdTenantLimit: cfg.householdTenantLimit ?? 10,
        kioskButtonCooldown: cfg.kioskButtonCooldown ?? 60,
        kioskNotificationGracePeriod: cfg.kioskNotificationGracePeriod ?? 60,
        tenantAccountApprove: cfg.tenantAccountApprove === true,
        useTwilioNotification: cfg.useTwilioNotification !== false,
        visitorMaxDays: cfg.visitorMaxDays ?? 7,
        // rev 93 — version gate (Gap C). Object form: { ios, android, ipad }.
        // Default-empty so the row renders cleanly when a site is mid-migration.
        minSupportedAppVersion: cfg.minSupportedAppVersion || {},
      },
    };
  });
  rows.sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, {
      sensitivity: 'base',
    }),
  );
  return rows;
};

// URL constants used in the derived siteURL block. Kept as module
// constants so a future change (e.g. new VMS host) is a single edit.
const VMS_BASE_URL = 'https://atact-connect-vms.vercel.app';
const MANAGEMENT_BASE_URL = 'https://atactconnect.web.app';

function buildSiteURL({ slug, displayName }) {
  return {
    account: `${VMS_BASE_URL}/account?site=${encodeURIComponent(displayName)}`,
    management: MANAGEMENT_BASE_URL,
    vms: `${VMS_BASE_URL}/?slug=${encodeURIComponent(slug)}`,
  };
}

/**
 * Create a new site doc. siteId is an auto-generated Firestore UUID.
 * `slug` is required, must be unique across sites, and IS NOT EDITABLE
 * after creation — used in URLs and downstream integrations, so a later
 * rename would orphan external references.
 *
 * Schema (per prod inspection 2026-05-15):
 *   {
 *     displayName, slug, active,
 *     configuration: { householdTenantLimit, kioskButtonCooldown,
 *                      kioskNotificationGracePeriod, tenantAccountApprove,
 *                      useTwilioNotification, visitorMaxDays },
 *     information: { address, doorChannel: {}, siteChannel, siteType },
 *     metadata: { createdAt, updatedAt },
 *     siteDetail: { configuration: { rejectionReasons: [] } },
 *     siteURL: { account, management, vms }
 *   }
 *
 * @param {Object} payload — see destructuring below.
 * @returns {Promise<string>} the new site's doc id (UUID).
 */
export const firCreateSite = async (payload) => {
  if (!payload.slug || !payload.slug.trim()) {
    throw new Error('slug is required');
  }
  const slug = payload.slug.trim();
  const { displayName } = payload;
  const siteChannel = `channel_${slug}`; // mobile pub/sub topic — derived from slug
  const newRef = doc(collection(db, 'sites'));
  await setDoc(newRef, {
    displayName,
    slug,
    active: payload.active ?? false,
    configuration: {
      householdTenantLimit: payload.householdTenantLimit ?? 10,
      kioskButtonCooldown: payload.kioskButtonCooldown ?? 60,
      kioskNotificationGracePeriod: payload.kioskNotificationGracePeriod ?? 60,
      tenantAccountApprove: payload.tenantAccountApprove ?? false,
      useTwilioNotification: payload.useTwilioNotification ?? true,
      visitorMaxDays: payload.visitorMaxDays ?? 7,
    },
    information: {
      address: payload.address ?? '',
      doorChannel: {},
      siteChannel,
      siteType: payload.siteType ?? '',
    },
    metadata: {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    siteDetail: {
      configuration: {
        // TODO(D24-staff): rejectionReasons is edited by per-site admin
        // via Site Config -> Account Application (rev 73 P3).
        // Other deferred per-site fields:
        //   - householdTenantLimit per-role breakdown (1 main owner /
        //     3 sub owner / 1 main tenant / 3 sub tenant) — currently
        //     we only persist the flat configuration.householdTenantLimit.
        //   - accountApplicationDocTypes per-role customisation
        rejectionReasons: [],
      },
    },
    siteURL: buildSiteURL({ slug, displayName }),
  });
  debugLog('-- firCreateSite success', newRef.id);
  return newRef.id;
};

/**
 * Partial update on a site doc. Only the fields included in `patch`
 * are written. Use dotted keys for nested fields. Always bumps
 * `metadata.updatedAt` server-side; if `displayName` is in the patch,
 * `siteURL.account` (which embeds the URL-encoded display name) is
 * recomputed alongside.
 *
 * @param {string} siteId
 * @param {Object} patch
 */
export const firUpdateSite = async (siteId, patch) => {
  const finalPatch = {
    ...patch,
    'metadata.updatedAt': serverTimestamp(),
  };
  if (patch.displayName) {
    finalPatch['siteURL.account'] =
      `${VMS_BASE_URL}/account?site=${encodeURIComponent(patch.displayName)}`;
  }
  await updateDoc(doc(db, 'sites', siteId), finalPatch);
  debugLog('-- firUpdateSite success', siteId);
};

/**
 * Toggle just the `active` flag. Convenience wrapper used by the
 * inline Switch in the All Sites table.
 */
export const firSetSiteActive = async (siteId, active) => {
  await updateDoc(doc(db, 'sites', siteId), { active: !!active });
};

/**
 * rev 95 (2026-05-17) — read the global mobile version gate.
 *
 * Returns `{ ios, android, ipad }`. Missing fields default to empty.
 * The same App Store / Play Store binary serves every site, so this
 * lives in a single project-wide doc `config/global` rather than
 * per-site (which was rev 94's mistake).
 *
 * @returns {Promise<{ ios: string, android: string, ipad: string }>}
 */
export const firGetGlobalVersionGate = async () => {
  const snap = await getDoc(doc(db, 'config', 'global'));
  const g = snap.exists() ? snap.data()?.minSupportedAppVersion || {} : {};
  return {
    ios: g.ios || '',
    android: g.android || '',
    ipad: g.ipad || '',
  };
};

/**
 * rev 95 (2026-05-17) — write the global mobile version gate.
 *
 * Bumping these values BLOCKS every install on a lower version the
 * next time it launches. The MA web caller (Mobile Version Gate tab)
 * gates this behind a type-to-confirm phrase.
 *
 * `_lastWriteBy` is consumed + cleared by `v2auditConfigChanges`,
 * which emits a `global_version_gate_bump` row into `auditLog/`.
 *
 * @param {{ ios: string, android: string, ipad: string }} gate
 * @param {string} byUid  caller's primaryAuth.currentUser.uid
 */
export const firUpdateGlobalVersionGate = async (gate, byUid) => {
  const patch = {
    'minSupportedAppVersion.ios': gate.ios ?? '',
    'minSupportedAppVersion.android': gate.android ?? '',
    'minSupportedAppVersion.ipad': gate.ipad ?? '',
    updatedAt: serverTimestamp(),
    _lastWriteBy: byUid,
  };
  await updateDoc(doc(db, 'config', 'global'), patch);
  debugLog('-- firUpdateGlobalVersionGate success', gate);
};

// rev 94 helper kept exported but deprecated — no UI caller as of
// rev 95. Safe to delete once we're sure no external script imports it.
export const firUpdateSiteVersionGate = async (siteId, gate, byUid) => {
  const patch = {
    'configuration.minSupportedAppVersion.ios': gate.ios ?? '',
    'configuration.minSupportedAppVersion.android': gate.android ?? '',
    'configuration.minSupportedAppVersion.ipad': gate.ipad ?? '',
    'metadata.updatedAt': serverTimestamp(),
    _lastWriteBy: byUid,
  };
  await updateDoc(doc(db, 'sites', siteId), patch);
  debugLog(
    '-- firUpdateSiteVersionGate success (deprecated since rev 95)',
    siteId,
    gate,
  );
};

/**
 * Add a brand-new door channel (staff install path). Will not
 * overwrite an existing channel with the same code — caller should
 * check or rely on a UI confirmation.
 */
export const firAddDoorChannel = async (siteId, channelCode, fields) => {
  await updateDoc(doc(db, 'sites', siteId), {
    [`information.doorChannel.${channelCode}`]: {
      label: fields.label,
      isVisitorEntry: !!fields.isVisitorEntry,
    },
  });
  debugLog('-- firAddDoorChannel success', siteId, channelCode);
};

/**
 * Remove a door channel entirely (staff decommission path). Use with
 * care — any unit / visit referencing this channel will break.
 */
export const firRemoveDoorChannel = async (siteId, channelCode) => {
  await updateDoc(doc(db, 'sites', siteId), {
    [`information.doorChannel.${channelCode}`]: deleteField(),
  });
  debugLog('-- firRemoveDoorChannel success', siteId, channelCode);
};
